import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { RegistrarMovimiento } from './RegistrarMovimiento';

interface Puesto {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
}

interface Turno {
  readonly id: string;
  readonly numero: string;
  readonly puestoDeCajaId: string;
  readonly puesto?: string;
  readonly estado: 'abierto' | 'cerrado';
  readonly abiertoEn: string;
  readonly correoDeQuienAbrio: string | null;
  readonly cerradoEn: string | null;
  readonly correoDeQuienCerro: string | null;
  readonly efectivoEsperado: string | null;
  readonly efectivoDeclarado: string | null;
  readonly diferencia: string | null;
}

interface MovimientoDeCaja {
  readonly id: string;
  readonly tipo: string;
  readonly medioDePago: string;
  readonly importe: string;
  readonly concepto: string;
  readonly ocurridoEn: string;
  readonly correoDelUsuario: string | null;
  /** La contrapartida con que se asentó (E37). Nula en lo que no es un gasto o ingreso a mano. */
  readonly cuentaContableId: string | null;
  readonly referenciaTipo: string | null;
  readonly referenciaId: string | null;
}

/**
 * Si el movimiento se puede revertir desde acá: un gasto o un ingreso a mano, clasificado, que no
 * sea él mismo una reversión y que nadie haya revertido todavía. Lo demás se deshace desde su
 * comprobante, y lo de antes de E37 no tiene asiento que dar vuelta.
 */
function sePuedeRevertir(fila: MovimientoDeCaja, todos: readonly MovimientoDeCaja[]): boolean {
  return (
    (fila.tipo === 'ingreso' || fila.tipo === 'egreso') &&
    fila.cuentaContableId !== null &&
    fila.referenciaTipo === null &&
    !todos.some(
      (otro) => otro.referenciaTipo === 'movimiento_de_caja' && otro.referenciaId === fila.id,
    )
  );
}

interface ArqueoDelTurno {
  readonly efectivoEsperado: string;
  readonly porMedio: readonly { medio: string; total: string }[];
}

/** `diferencia_de_arqueo` → `caja.tipoDiferenciaDeArqueo`. */
function claveTraducida(prefijo: string, valor: string): string {
  const camello = valor.replace(/_(.)/g, (_, letra: string) => letra.toUpperCase());
  return `caja.${prefijo}${camello.charAt(0).toUpperCase()}${camello.slice(1)}`;
}

/** Los importes se guardan con cuatro decimales; en el mostrador se miran con dos. */
function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * Caja: abrir el turno, mover plata, arquear y cerrar.
 *
 * La pantalla está armada alrededor de una idea: **el cierre es una declaración de una
 * persona, no un cálculo del sistema**. Por eso el número esperado y el contado se muestran
 * juntos, la diferencia aparece con su signo y su nombre, y no hay ningún botón que
 * "ajuste" la caja para que cuadre. Una caja que siempre cuadra perfecto no es una caja
 * bien llevada: es una donde alguien emparejó los números.
 */
export function Caja(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeAbrir = usePermiso('CAJA.TURNO.ABRIR');
  const puedeCerrar = usePermiso('CAJA.TURNO.CERRAR');
  const puedeMover = usePermiso('CAJA.MOVIMIENTO.REGISTRAR');

  const [puestoId, setPuestoId] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [aperturaAbierta, setAperturaAbierta] = useState(false);
  const [saldoInicial, setSaldoInicial] = useState('');

  const [movimientoAbierto, setMovimientoAbierto] = useState(false);
  const [revirtiendo, setRevirtiendo] = useState<MovimientoDeCaja | null>(null);

  const [cierreAbierto, setCierreAbierto] = useState(false);
  const [declarado, setDeclarado] = useState('');
  const [observacion, setObservacion] = useState('');

  const puestos = useQuery({
    queryKey: ['puestos-de-caja'],
    queryFn: () => pedir<Puesto[]>('/puestos-de-caja'),
  });

  const puestoActivo = puestoId === '' ? (puestos.data?.[0]?.id ?? '') : puestoId;

  const turno = useQuery({
    queryKey: ['turno-abierto', puestoActivo],
    enabled: puestoActivo !== '',
    queryFn: () => pedir<Turno | null>(`/puestos-de-caja/${puestoActivo}/turno-abierto`),
  });

  const turnoId = turno.data?.id ?? '';

  const movimientos = useQuery({
    queryKey: ['movimientos-de-caja', turnoId],
    enabled: turnoId !== '',
    queryFn: () => pedir<MovimientoDeCaja[]>(`/turnos-de-caja/${turnoId}/movimientos`),
  });

  const arqueo = useQuery({
    queryKey: ['arqueo', turnoId],
    enabled: turnoId !== '',
    queryFn: () => pedir<ArqueoDelTurno>(`/turnos-de-caja/${turnoId}/arqueo`),
  });

  const historia = useQuery({
    queryKey: ['turnos-de-caja'],
    queryFn: () => pedir<Turno[]>('/turnos-de-caja'),
  });

  async function refrescar(): Promise<void> {
    await Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: ['turno-abierto'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['movimientos-de-caja'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['arqueo'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['turnos-de-caja'] }),
    ]);
  }

  const abrir = useMutation({
    mutationFn: () =>
      pedir<{ turnoId: string; numero: string }>('/turnos-de-caja', {
        method: 'POST',
        cuerpo: {
          puestoDeCajaId: puestoActivo,
          saldoInicial: saldoInicial.trim().replace(',', '.'),
        },
      }),
    onSuccess: async () => {
      setAperturaAbierta(false);
      setSaldoInicial('');
      setError(null);
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const revertir = useMutation({
    mutationFn: (movimientoId: string) =>
      pedir<{ id: string }>(`/movimientos-de-caja/${movimientoId}/reversion`, { method: 'POST' }),
    onSuccess: async () => {
      setRevirtiendo(null);
      setError(null);
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const cerrar = useMutation({
    mutationFn: () =>
      pedir<{ esperado: string; declarado: string; diferencia: string }>(
        `/turnos-de-caja/${turnoId}/cierre`,
        {
          method: 'POST',
          cuerpo: {
            efectivoDeclarado: declarado.trim().replace(',', '.'),
            ...(observacion.trim() === '' ? {} : { observacion: observacion.trim() }),
          },
        },
      ),
    onSuccess: async (resultado) => {
      setCierreAbierto(false);
      setDeclarado('');
      setObservacion('');
      setError(null);
      setAviso(
        t('caja.cerrado', {
          esperado: conDosDecimales(resultado.esperado),
          declarado: conDosDecimales(resultado.declarado),
          diferencia: conDosDecimales(resultado.diferencia),
        }),
      );
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const columnas: Columna<MovimientoDeCaja>[] = [
    {
      clave: 'cuando',
      titulo: t('caja.cuando'),
      contenido: (fila) => fila.ocurridoEn.slice(11, 16),
    },
    {
      clave: 'tipo',
      titulo: t('caja.tipo'),
      contenido: (fila) => (
        <>
          {t(claveTraducida('tipo', fila.tipo))}
          <span className="block text-xs text-texto-tenue">{fila.concepto}</span>
        </>
      ),
    },
    {
      clave: 'medio',
      titulo: t('caja.medioDePago'),
      contenido: (fila) => t(`caja.${fila.medioDePago}`),
    },
    {
      clave: 'importe',
      titulo: t('caja.importe'),
      numerica: true,
      contenido: (fila) => (
        <span className={`numero ${fila.importe.startsWith('-') ? 'text-mal' : 'text-bien'}`}>
          {conDosDecimales(fila.importe)}
        </span>
      ),
    },
    {
      clave: 'quien',
      titulo: t('caja.quien'),
      contenido: (fila) => fila.correoDelUsuario ?? '—',
    },
    {
      clave: 'acciones',
      titulo: t('cuentas.acciones'),
      contenido: (fila) =>
        puedeMover && sePuedeRevertir(fila, movimientos.data ?? []) ? (
          <Boton
            onClick={() => {
              setRevirtiendo(fila);
              setError(null);
            }}
          >
            {t('caja.revertir')}
          </Boton>
        ) : null,
    },
  ];

  const columnasDeHistoria: Columna<Turno>[] = [
    { clave: 'numero', titulo: t('caja.turno'), contenido: (fila) => fila.numero },
    { clave: 'puesto', titulo: t('caja.puesto'), contenido: (fila) => fila.puesto ?? '—' },
    {
      clave: 'estado',
      titulo: t('caja.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.estado === 'abierto' ? 'atencion' : 'neutro'}>
          {fila.estado === 'abierto' ? t('caja.estadoAbierto') : t('caja.estadoCerrado')}
        </Insignia>
      ),
    },
    {
      clave: 'esperado',
      titulo: t('caja.esperado'),
      numerica: true,
      contenido: (fila) => (
        <span className="numero">
          {fila.efectivoEsperado === null ? '—' : conDosDecimales(fila.efectivoEsperado)}
        </span>
      ),
    },
    {
      clave: 'declarado',
      titulo: t('caja.declarado'),
      numerica: true,
      contenido: (fila) => (
        <span className="numero">
          {fila.efectivoDeclarado === null ? '—' : conDosDecimales(fila.efectivoDeclarado)}
        </span>
      ),
    },
    {
      clave: 'diferencia',
      titulo: t('caja.diferencia'),
      numerica: true,
      contenido: (fila) => {
        if (fila.diferencia === null) return <span className="numero">{'—'}</span>;
        const cero = Number(fila.diferencia) === 0;
        return (
          <span className={`numero ${cero ? '' : 'text-mal'}`}>
            {conDosDecimales(fila.diferencia)}
          </span>
        );
      },
    },
    {
      clave: 'cerro',
      titulo: t('caja.quien'),
      contenido: (fila) => fila.correoDeQuienCerro ?? '—',
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('caja.titulo')}
        descripcion={t('caja.descripcion')}
        acciones={
          turno.data === null && puedeAbrir ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAperturaAbierta(true);
                setError(null);
              }}
            >
              {t('caja.abrir')}
            </Boton>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4">
        <Tarjeta>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Selector
                etiqueta={t('caja.puesto')}
                value={puestoActivo}
                onChange={(evento) => {
                  setPuestoId(evento.target.value);
                  setAviso(null);
                }}
              >
                {(puestos.data ?? []).map((puesto) => (
                  <option key={puesto.id} value={puesto.id}>
                    {puesto.nombre}
                  </option>
                ))}
              </Selector>
            </div>

            {turno.data === null || turno.data === undefined ? null : (
              <>
                <Insignia tono="atencion">
                  {t('caja.turnoNumero', { numero: turno.data.numero })}
                </Insignia>
                <span className="text-xs text-texto-tenue">
                  {t('caja.abiertoPor', { quien: turno.data.correoDeQuienAbrio ?? '—' })}
                </span>
              </>
            )}

            {aviso === null ? null : (
              <p role="status" className="text-xs text-bien">
                {aviso}
              </p>
            )}
          </div>
        </Tarjeta>

        {turno.isPending ? <Cargando /> : null}
        {turno.isError ? (
          <Problema
            error={turno.error}
            reintentar={() => {
              void turno.refetch();
            }}
          />
        ) : null}

        {turno.data === null ? (
          <Tarjeta>
            <Vacio descripcion={t('caja.sinTurno')} />
          </Tarjeta>
        ) : null}

        {turnoId === '' ? null : (
          <>
            <Tarjeta
              titulo={t('caja.arqueo')}
              descripcion={t('caja.ayudaArqueo')}
              acciones={
                <>
                  {puedeMover ? (
                    <Boton
                      onClick={() => {
                        setMovimientoAbierto(true);
                        setError(null);
                      }}
                    >
                      {t('caja.movimiento')}
                    </Boton>
                  ) : null}
                  {puedeCerrar ? (
                    <Boton
                      tono="principal"
                      onClick={() => {
                        setCierreAbierto(true);
                        setError(null);
                      }}
                    >
                      {t('caja.cerrar')}
                    </Boton>
                  ) : null}
                </>
              }
            >
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-xs text-texto-tenue">{t('caja.esperado')}</p>
                  <p className="numero text-2xl font-semibold">
                    {conDosDecimales(arqueo.data?.efectivoEsperado ?? '0')}
                  </p>
                </div>
                {/* Los demás medios se muestran al lado y en chico: hacen falta para
                    conciliar, pero no se cuentan al cerrar. */}
                {(arqueo.data?.porMedio ?? [])
                  .filter((fila) => fila.medio !== 'efectivo')
                  .map((fila) => (
                    <div key={fila.medio}>
                      <p className="text-xs text-texto-tenue">{t(`caja.${fila.medio}`)}</p>
                      <p className="numero text-sm">{conDosDecimales(fila.total)}</p>
                    </div>
                  ))}
              </div>
            </Tarjeta>

            <Tarjeta titulo={t('caja.movimientos')}>
              {movimientos.isPending ? <Cargando /> : null}
              {movimientos.isSuccess ? (
                <Tabla
                  columnas={columnas}
                  filas={movimientos.data}
                  claveDeFila={(fila) => fila.id}
                  vacio={<Vacio descripcion={t('caja.sinMovimientos')} />}
                />
              ) : null}
            </Tarjeta>
          </>
        )}

        <Tarjeta titulo={t('caja.historia')}>
          {historia.isPending ? <Cargando /> : null}
          {historia.isSuccess ? (
            <Tabla
              columnas={columnasDeHistoria}
              filas={historia.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio descripcion={t('caja.sinTurnos')} />}
            />
          ) : null}
        </Tarjeta>
      </div>

      <Dialogo
        abierto={aperturaAbierta}
        titulo={t('caja.abrir')}
        onCerrar={() => {
          setAperturaAbierta(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={abrir.isPending}
            disabled={saldoInicial.trim() === ''}
            onClick={() => {
              abrir.mutate();
            }}
          >
            {t('acciones.confirmar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('caja.saldoInicial')}
            obligatorio
            inputMode="decimal"
            value={saldoInicial}
            ayuda={t('caja.ayudaSaldoInicial')}
            onChange={(evento) => {
              setSaldoInicial(evento.target.value);
            }}
          />
          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      {turnoId === '' ? null : (
        <RegistrarMovimiento
          turnoId={turnoId}
          caja={{
            puestoDeCajaId: puestoActivo,
            nombre: puestos.data?.find((puesto) => puesto.id === puestoActivo)?.nombre ?? '',
          }}
          abierto={movimientoAbierto}
          onCerrar={() => {
            setMovimientoAbierto(false);
          }}
        />
      )}

      <Dialogo
        abierto={revirtiendo !== null}
        titulo={t('caja.revertir')}
        onCerrar={() => {
          setRevirtiendo(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={revertir.isPending}
            onClick={() => {
              if (revirtiendo !== null) revertir.mutate(revirtiendo.id);
            }}
          >
            {t('acciones.confirmar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            {t('caja.confirmarReversion', { concepto: revirtiendo?.concepto ?? '' })}
          </p>
          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={cierreAbierto}
        titulo={t('caja.cerrar')}
        onCerrar={() => {
          setCierreAbierto(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={cerrar.isPending}
            disabled={declarado.trim() === ''}
            onClick={() => {
              cerrar.mutate();
            }}
          >
            {t('acciones.confirmar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs text-texto-tenue">{t('caja.esperado')}</p>
            <p className="numero text-xl font-semibold">
              {conDosDecimales(arqueo.data?.efectivoEsperado ?? '0')}
            </p>
          </div>

          <Campo
            etiqueta={t('caja.declarado')}
            obligatorio
            inputMode="decimal"
            value={declarado}
            ayuda={t('caja.ayudaDeclarado')}
            onChange={(evento) => {
              setDeclarado(evento.target.value);
            }}
          />

          {/* La diferencia se muestra antes de confirmar, con su palabra: nadie tendría que
              enterarse de que le faltan dos mil pesos después de haber apretado el botón. */}
          {declarado.trim() === '' ? null : (
            <p className="text-sm">
              {(() => {
                const cuenta =
                  Number(declarado.replace(',', '.')) -
                  Number(arqueo.data?.efectivoEsperado ?? '0');
                if (Number.isNaN(cuenta)) return null;
                if (cuenta === 0) return <span className="text-bien">{t('caja.cuadra')}</span>;
                const palabra = cuenta > 0 ? t('caja.sobra') : t('caja.falta');
                return (
                  <span className="text-mal">
                    {palabra} <span className="numero">{Math.abs(cuenta).toFixed(2)}</span>
                  </span>
                );
              })()}
            </p>
          )}

          <Campo
            etiqueta={t('caja.observacion')}
            maxLength={400}
            value={observacion}
            ayuda={t('caja.ayudaObservacion')}
            onChange={(evento) => {
              setObservacion(evento.target.value);
            }}
          />

          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
